import React, { useCallback, useMemo, useState } from 'react';
import AvTable from '@components/Table/AvTable';
import { useAvContext } from '@context/AvContextProvider';
import { AvButton, AvTooltip, ColumnReorder, ExportButton } from '@fabric-ui/components';
import { ActionButtonV2 } from '@fabric-ui/components/Table';
import { External, X } from '@fabric-ui/icons';
import { flex, shadowPrimaryBottomSm } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { useExportQueryObjectWorkflow } from '@hooks/useExportQueryWorklflow';
import useQueryObject from '@hooks/useQueryObjectSql';
import { Box, Divider, Skeleton, Typography, useTheme } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { appNames } from '@utils/appNames';
import { useLocation, useNavigate, useParams } from '@utils/AvRouter';
import { PAGE_PATHS } from '../../../types';
import AgentCategoryTag from './AgentCategoryTag';
import { getAlertsListByAgent, getAllAgents } from './queries';
import { AgentDetailsFilterProps } from './types';
import {
  AgentDetailAttribute,
  AgentDetailMetric,
  AgentDetailSection,
  AgentJobDescriptionSection,
  agentTableCommonStyle,
  flattenToQueryString,
  initialAgentHeadCells,
  MitreColumnMap,
  NoDataFoundWidget,
  useAlertListHeadCells,
} from './utils';

const SIDEBAR_WIDTH = 'calc(max(25%, 550px))';
const PAGE_SIZE = 100;

const AgentDetails: React.FC<AgentDetailsFilterProps> = ({ filterContext }) => {
  const theme = useTheme();
  const { getPathName } = useAvContext();
  const { id } = useParams();
  const navigate = useNavigate();
  const headCellsFromMemo = useAlertListHeadCells();
  const [hCells, setHCells] = useState(() => headCellsFromMemo);
  const [activeKey, setActiveKey] = useState();
  const [page, setPage] = useState(0);

  // Extract the state passed during navigation
  const {
    state: { name, description, category, outcome, count, actions, primaryOutcome = [], secondaryOutcome = [] },
  } = useLocation();

  const alertsListByAgentQuery = useMemo(
    () => getAlertsListByAgent(filterContext, id, hCells, page, PAGE_SIZE),
    [filterContext, id, hCells, page]
  );
  const agentDetailsQuery = useMemo(() => getAllAgents(id), [id]);

  const { mutate: exportQueryObjectWorkflow, isPending: isExportingWorkflow } = useExportQueryObjectWorkflow();
  const {
    data: alertsList,
    isLoading: isAlertsListLoading,
    totalRowCount,
  } = useQueryObject({
    queryObject: alertsListByAgentQuery,
    totalRowCount: true,
    options: { placeholderData: keepPreviousData },
    onSuccess: data => data ?? null,
  });

  const { data: agentDetails = {}, isLoading: isAlertDetailsLoading } = useQueryObject({
    queryObject: agentDetailsQuery,
    options: { placeholderData: keepPreviousData },
    onSuccess: data => data[0] ?? null,
  });

  // Export alerts list
  const exportReport = useCallback(() => {
    const {
      select: { dims },
    } = alertsListByAgentQuery;

    const enabledColumnIds = new Set(hCells.filter(cell => !cell.hidden).map(cell => cell.id));
    const mitreNames = Object.values(MitreColumnMap).map(m => m.name);

    const optimizedDims = dims
      .filter(item => enabledColumnIds.has(item.name) || mitreNames.includes(item.name))
      .map(dim => {
        const mitreMatch = Object.values(MitreColumnMap).find(m => m.name === dim.name);
        return mitreMatch ? { name: mitreMatch.name, alias: mitreMatch.alias } : dim;
      });

    exportQueryObjectWorkflow({
      queryObject: {
        ...alertsListByAgentQuery,
        select: {
          ...alertsListByAgentQuery.select,
          dims: optimizedDims,
          metrics: [],
        },
        timezoneOffset: new Date().getTimezoneOffset(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    });
  }, [alertsListByAgentQuery, hCells, exportQueryObjectWorkflow]);

  const navigateToAlerts = () => {
    const searchParams = { 'agents._key': id, ticketCategory: [appNames.Threats] };
    window.open(getPathName(PAGE_PATHS.ALERTS, `?${flattenToQueryString(searchParams)}`).to, '_blank');
  };

  // Redirect to alerts page for single alert
  const gotoEntityRowInlineAction = (row: Record<string, any>) => {
    const searchParams = { 'alert._key': row.key, ticketCategory: [appNames.Threats] };
    const queryString = flattenToQueryString(searchParams);
    return ActionButtonV2({
      label: `Open Alert in Alerts page`,
      onClick: () => {
        window.open(getPathName(PAGE_PATHS.ALERTS, `?${queryString}`).to, '_blank');
      },
      icon: <External />,
    });
  };

  // Close agent details
  const handleClose = useCallback(() => {
    navigate({
      from: '/',
      to: '/$accountId/threats/agents',
    });
  }, [navigate]);

  const alertsListCount = totalRowCount ?? alertsList?.length ?? 0;
  const noAlertsInvestigated = count === 0 || (!isAlertsListLoading && alertsListCount === 0);

  const agentOutcome = useMemo(() => {
    const primary = primaryOutcome?.[0] || agentDetails?.primaryOutcome?.[0];
    const secondary = secondaryOutcome?.[0] || agentDetails?.secondaryOutcome?.[0];
    return [primary, secondary].filter(Boolean);
  }, [primaryOutcome, secondaryOutcome, agentDetails]);

  return (
    <Box
      sx={{
        ...agentTableCommonStyle,
        height: 'calc(100% - 24px)',
        boxShadow: shadowPrimaryBottomSm,
        mx: 1,
        mb: 1,
      }}>
      <Box sx={{ width: SIDEBAR_WIDTH, minWidth: SIDEBAR_WIDTH, ...flex.col, p: 3, pt: 2, height: '100%', overflowY: 'auto' }}>
        <Box sx={{ ...flex.itemsBaseline, justifyContent: 'space-between' }}>
          {isAlertDetailsLoading && (!name || !category) ? (
            <Skeleton sx={{ flex: 1 }} />
          ) : (
            <>
              <Typography variant="h5" sx={{ py: '5px', m: 0, flex: 2 }}>
                {name || (agentDetails?.name ?? '')}
              </Typography>
              <AgentCategoryTag category={category || agentDetails?.category} />
            </>
          )}
        </Box>
        {/* Agent details Attribute Sections */}
        <AgentDetailAttribute attributes={agentOutcome} isLoading={isAlertDetailsLoading && !outcome} title="Outcome" />

        {/* Agent details Metrics Sections */}
        <Box sx={{ gap: 1, ...flex.row, justifyContent: 'space-between' }}>
          <AgentDetailMetric
            title="Alerts Processed"
            count={alertsListCount ?? count ?? 0}
            isLoading={count === undefined && isAlertsListLoading}
          />
        </Box>
        {/* Agent details Sections */}
        <AgentDetailSection
          title="Job Description"
          description={(description || agentDetails?.description) ?? ''}
          isLoading={isAlertDetailsLoading && !description}
        />

        {/* Agent Job Description Sections */}
        <AgentJobDescriptionSection
          title="Work Details"
          actions={(actions || agentDetails?.actions) ?? []}
          isLoading={isAlertDetailsLoading && !actions}
        />
      </Box>

      <Divider flexItem orientation="vertical" sx={{ borderColor: 'var(--color-border-base-primary)' }} />

      <Box sx={{ flex: 1, overflow: 'hidden', p: 3, pt: 2, gap: 1, width: '100%', height: 'calc(100% - 8px)' }}>
        <Box
          sx={{
            ...(!noAlertsInvestigated ? flex.justifyBetweenStart : flex.justifyEnd),
            alignItems: 'center',
            xs: { gap: '24px' },
            [theme.breakpoints.up(1280)]: { gap: '24px' },
            [theme.breakpoints.up(1355)]: { gap: '28px' },
            [theme.breakpoints.up('laptop')]: { gap: '32px' },
            [theme.breakpoints.up(1680)]: { gap: '36px' },
            [theme.breakpoints.up('desktop')]: { gap: '40px' },
            mb: 2,
          }}>
          {!noAlertsInvestigated && (
            <Box sx={{ ...flex.itemsCenter }}>
              <Typography variant="h5">
                Alerts (
                {isAlertsListLoading ? <Skeleton sx={{ display: 'inline-block', width: 30 }} /> : (alertsListCount ?? 0).toLocaleString()})
              </Typography>
            </Box>
          )}
          <Box sx={{ ...flex.itemsCenter, gap: 1 }}>
            {!noAlertsInvestigated && (
              <>
                <AvTooltip title="Open List in Alerts page">
                  <AvButton sx={{ gap: '4px', fontSize: 12 }} size="small" variant="primaryText" onClick={navigateToAlerts}>
                    Open List in Alerts page
                    <External style={iconSize(16)} />
                  </AvButton>
                </AvTooltip>
                <ExportButton width={14} loading={isExportingWorkflow} onClick={exportReport} />
                <ColumnReorder items={hCells} width={16} setItems={setHCells} initialColumns={initialAgentHeadCells} />

                <Divider orientation="vertical" variant="middle" sx={{ height: 16 }} flexItem />
              </>
            )}
            <AvTooltip title="Close Agent Details" sx={{ ml: 'auto' }}>
              <AvButton variant="filter" className="transparent" sx={{ px: 1, mr: -1 }} onClick={handleClose}>
                <X style={iconSize(20)} />
              </AvButton>
            </AvTooltip>
          </Box>
        </Box>

        {!noAlertsInvestigated ? (
          <AvTable
            storageKey="agent_details"
            rows={alertsList}
            headCells={[...hCells]}
            loading={isAlertsListLoading}
            resizable
            size="xSmall"
            allowEdit={false}
            enableSelectionColor={false}
            hasHeaderFilters={false}
            pagination
            rowLimit={PAGE_SIZE}
            paginationProps={{
              pageNum: page,
              onPageChange: setPage,
              totalRows: alertsListCount,
            }}
            sx={{
              height: 'calc(100% - 16px)',
              '.MuiTableCell-head': {
                backgroundColor: 'var(--color-surface-base-primary)',
              },
            }}
            inlineActions={[gotoEntityRowInlineAction]}
            activeKey={activeKey}
            onActive={setActiveKey}
          />
        ) : (
          <NoDataFoundWidget />
        )}
      </Box>
    </Box>
  );
};

export default AgentDetails;
